import { useEffect, useRef, useState } from "react";
import { api, brl, API } from "../lib/api";
import { uploadFoto } from "../lib/image";
import { toast } from "sonner";
import { connectWS } from "../lib/ws";
import { inscreverPush } from "../lib/push";
import {
  Truck, LogOut, MapPin, Navigation, CheckCircle2, XCircle, Loader2,
  Wallet, Clock, AlertTriangle, Satellite, Star, Camera, Lock, Hourglass,
  Eye, EyeOff, Ban, ScrollText, Siren, FileEdit,
} from "lucide-react";

const hdr = (t) => ({ Authorization: `Bearer ${t}` });

// Alerta sonoro: buzina pneumática de carreta (arquivo /buzina-carreta.mp3, ~1,85s).
// O controle do ciclo contínuo fica dentro de DriverHome (iniciar/parar por solicitação).

function SenhaInput({ value, onChange, placeholder, testid, className }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testid} type={show ? "text" : "password"} className={`${className} pr-12`} placeholder={placeholder} value={value} onChange={onChange} />
      <button type="button" data-testid={`${testid}-toggle`} onClick={() => setShow((s) => !s)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy">
        {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
  );
}

const STATUS_LABEL = {
  disponivel: "DISPONÍVEL", aceita: "CORRIDA ACEITA", a_caminho: "A CAMINHO",
  chegou_origem: "CHEGOU NA ORIGEM", carga_iniciada: "CARGA INICIADA", em_rota: "EM ROTA",
  chegou_destino: "CHEGOU AO DESTINO", concluida: "SERVIÇO CONCLUÍDO", cancelada: "CANCELADO",
};
const NEXT_STATUS = {
  aceita: ["a_caminho", "SAIR PARA O LOCAL"],
  a_caminho: ["chegou_origem", "CHEGUEI NA ORIGEM"],
  chegou_origem: ["carga_iniciada", "INICIAR CARGA"],
  carga_iniciada: ["em_rota", "EM ROTA PARA O DESTINO"],
  em_rota: ["chegou_destino", "CHEGUEI AO DESTINO"],
  chegou_destino: ["concluida", "CONCLUIR SERVIÇO"],
};
const ACTIVE = ["aceita", "a_caminho", "chegou_origem", "carga_iniciada", "em_rota", "chegou_destino"];

const minus15 = (h) => {
  if (!h) return "-";
  const [hh, mm] = h.split(":").map(Number);
  const d = new Date(2000, 0, 1, hh, mm - 15);
  return d.toTimeString().slice(0, 5);
};
const fmtD = (s) => (s ? s.split("-").reverse().join("/") : "-");
const somenteDigitos = (s) => (s || "").replace(/\D/g, "");
const addrTxt = (a) => [a?.logradouro && a?.numero ? `${a.logradouro}, ${a.numero}` : "", a?.bairro, a?.cidade && a?.uf ? `${a.cidade} - ${a.uf}` : "", a?.cep].filter(Boolean).join(", ");
const mapsUrl = (a) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addrTxt(a))}`;
const wazeUrl = (a) => `https://waze.com/ul?q=${encodeURIComponent(addrTxt(a))}&navigate=yes`;

function HeaderMotorista({ driver, onLogout }) {
  return (
    <header className="sticky top-0 z-40 px-4 py-3 bg-navy border-b-2 border-[#F5B301]" data-testid="driver-header">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="bg-white rounded-lg p-1 shadow-md">
            <img src="/logo.jpg" alt="Carlos Jr" className="h-9 w-auto object-contain" />
          </div>
          <div>
            <p className="font-heading font-extrabold text-white text-sm leading-tight">Carlos Júnior</p>
            <p className="text-[10px] font-bold text-[#F5B301] uppercase tracking-wide -mt-0.5">Aplicativo Motorista</p>
            {driver && (
              <p className="text-[9px] font-bold text-slate-300 uppercase tracking-wide flex items-center gap-1" data-testid="perfil-motorista-label">
                <img src="/caminhao-amarelo.png" alt="" className="h-3.5 w-auto object-contain" /> Motorista / Prestador
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {driver?.avaliacao && (
            <span className="text-[11px] font-bold text-[#F5B301]" data-testid="driver-nota">★ {driver.avaliacao.nota.toFixed(1)}</span>
          )}
          {onLogout && (
            <button data-testid="driver-logout-btn" onClick={onLogout} className="p-2 text-slate-300 hover:text-red-400"><LogOut className="w-5 h-5" /></button>
          )}
        </div>
      </div>
    </header>
  );
}

function Login({ onLogin, onCadastro }) {
  const [login, setLogin] = useState("");
  const [senha, setSenha] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/driver/login", { login, senha });
      localStorage.setItem("driver_token", r.data.token);
      onLogin(r.data.driver);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no login");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-navy">
      <HeaderMotorista driver={null} onLogout={null} />
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm text-center">
          <img src="/logo.jpg" alt="Carlos Jr Fretes e Mudanças" className="h-24 w-auto object-contain mx-auto mb-5 rounded-xl bg-white p-1.5" />
          <p className="text-slate-300 text-xs mb-5">Acesso exclusivo com login e senha cadastrados pela administração.</p>
          <input data-testid="driver-login-input" className="w-full px-4 py-3.5 rounded-xl mb-3 text-sm" placeholder="Login (telefone ou e-mail)" value={login} onChange={(e) => setLogin(e.target.value)} />
          <div className="mb-4">
            <SenhaInput testid="driver-senha-input" className="w-full px-4 py-3.5 rounded-xl text-sm" placeholder="Senha" value={senha} onChange={(e) => setSenha(e.target.value)} />
          </div>
          <button data-testid="driver-login-btn" onClick={submit} disabled={loading || !login || !senha}
            className="w-full py-4 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-60">
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "ENTRAR"}
          </button>
          <button data-testid="cadastro-motorista-link" onClick={onCadastro}
            className="mt-3 w-full py-3.5 rounded-2xl border-2 border-dashed border-[#F5B301] text-[#F5B301] font-heading font-bold text-xs active:scale-95 transition-transform">
            CADASTRAR-SE COMO MOTORISTA / PRESTADOR
          </button>
          <a href="/meus-servicos" className="block mt-3 text-[11px] text-slate-400 underline" data-testid="voltar-cliente-link">
            É cliente? Consultar agendamento
          </a>
          <p className="mt-4 text-[10px] text-slate-500">carlosjrfretesemudancas.com.br</p>
        </div>
      </div>
    </div>
  );
}

function OnboardingSenha({ token, onDone }) {
  const [s1, setS1] = useState("");
  const [s2, setS2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (s1.length < 4) return toast.error("A senha deve ter pelo menos 4 caracteres");
    if (s1 !== s2) return toast.error("As senhas não coincidem");
    setBusy(true);
    try {
      await api.post("/driver/trocar-senha", { nova_senha: s1 }, { headers: hdr(token) });
      toast.success("Senha definida!");
      onDone("aguardando_vistoria");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao definir senha");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="px-5 py-10" data-testid="onboarding-senha">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-md p-6 text-center">
        <Lock className="w-10 h-10 text-navy mx-auto mb-3" />
        <h2 className="font-heading text-xl font-bold text-navy">Primeiro acesso</h2>
        <p className="text-sm text-slate-500 mt-2 mb-5">Você está usando uma senha temporária. Crie agora sua senha definitiva.</p>
        <div className="mb-3">
          <SenhaInput testid="nova-senha-input" className="w-full px-4 py-3.5 rounded-xl border border-slate-200 text-sm" placeholder="Nova senha" value={s1} onChange={(e) => setS1(e.target.value)} />
        </div>
        <div className="mb-4">
          <SenhaInput testid="nova-senha2-input" className="w-full px-4 py-3.5 rounded-xl border border-slate-200 text-sm" placeholder="Repita a nova senha" value={s2} onChange={(e) => setS2(e.target.value)} />
        </div>
        <button data-testid="definir-senha-btn" onClick={submit} disabled={busy}
          className="w-full py-4 rounded-2xl bg-navy text-[#F5B301] font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-60">
          {busy ? "Salvando..." : "DEFINIR SENHA"}
        </button>
      </div>
    </div>
  );
}

const FOTOS_VISTORIA = [
  ["cnh", "Habilitação (CNH)"],
  ["crlv", "CRLV — documento do veículo"],
  ["frente", "Frente do caminhão"],
  ["traseira", "Traseira do caminhão"],
  ["lateral_esq", "Lateral esquerda"],
  ["lateral_dir", "Lateral direita"],
  ["bau_aberto", "Baú aberto (visão interna)"],
];
const FOTOS_VEICULO = FOTOS_VISTORIA.filter(([k]) => k !== "cnh" && k !== "crlv");
const DOCS_DUAL = FOTOS_VISTORIA.filter(([k]) => k === "cnh" || k === "crlv");
const PORTES = [["pequeno", "Pequeno Frete"], ["medio", "Médio Frete"], ["grande", "Grande Frete"]];

function OnboardingVistoria({ token, onDone }) {
  const [placa, setPlaca] = useState(() => sessionStorage.getItem("cjr_vist_placa") || "");
  const [portes, setPortes] = useState(() => { try { return JSON.parse(sessionStorage.getItem("cjr_vist_portes") || "[]"); } catch { return []; } });
  const [fotos, setFotos] = useState(() => { try { return JSON.parse(sessionStorage.getItem("cjr_vist_fotos") || "{}"); } catch { return {}; } });
  const [uploading, setUploading] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { try { sessionStorage.setItem("cjr_vist_placa", placa); } catch {} }, [placa]);
  useEffect(() => { try { sessionStorage.setItem("cjr_vist_portes", JSON.stringify(portes)); } catch {} }, [portes]);
  useEffect(() => { try { sessionStorage.setItem("cjr_vist_fotos", JSON.stringify(fotos)); } catch {} }, [fotos]);

  const togglePorte = (p) => setPortes((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const upload = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const path = await uploadFoto(file, `vistoria:${key}`);
      setFotos((f) => ({ ...f, [key]: path }));
    } catch (e) {
      toast.error(e.message || "Não foi possível enviar a foto. Tente novamente.");
    } finally {
      setUploading("");
    }
  };

  const completo = placa.trim() && portes.length >= 1 && FOTOS_VISTORIA.every(([k]) => fotos[k]);

  const enviar = async () => {
    setBusy(true);
    try {
      await api.post("/driver/vistoria", { placa, portes, fotos }, { headers: hdr(token) });
      ["cjr_vist_placa", "cjr_vist_portes", "cjr_vist_fotos"].forEach((k) => sessionStorage.removeItem(k));
      toast.success("Vistoria enviada para análise!");
      onDone("aguardando_aprovacao");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar vistoria");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="px-5 py-8 pb-16" data-testid="onboarding-vistoria">
      <h2 className="font-heading text-xl font-bold text-navy">Vistoria do veículo</h2>
      <p className="text-sm text-slate-500 mt-1 mb-5">
        Envie as fotos obrigatórias (todos os dados visíveis) e escolha os portes de frete que você aceita realizar.
      </p>

      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Quais fretes você aceita? (pode marcar 1, 2 ou todos)</p>
      <div className="grid grid-cols-3 gap-2 mb-5">
        {PORTES.map(([v, l]) => (
          <button key={v} data-testid={`porte-${v}`} onClick={() => togglePorte(v)}
            className={`py-3.5 rounded-xl border-2 text-xs font-bold active:scale-95 transition-all ${
              portes.includes(v) ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 bg-white text-slate-500"}`}>
            {l}
          </button>
        ))}
      </div>

      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Placa do veículo *</label>
      <input data-testid="placa-input" className="w-full px-4 py-3.5 rounded-xl border border-slate-200 mb-5 text-sm uppercase mt-1"
        placeholder="ABC1D23" maxLength={8} value={placa} onChange={(e) => setPlaca(e.target.value.toUpperCase())} />

      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Habilitação (CNH) — câmera ou arquivo/PDF *</p>
      <div className="grid grid-cols-2 gap-2 mb-4" data-testid="vistoria-cnh-opcoes">
        <label data-testid="vistoria-foto-cnh-camera"
          className={`flex flex-col items-center gap-1.5 p-3.5 rounded-xl border-2 cursor-pointer text-center ${fotos.cnh ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300 bg-white"}`}>
          {uploading === "cnh" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <Camera className="w-5 h-5 text-navy" />}
          <span className="text-[10px] font-bold text-navy">📷 Tirar foto da CNH</span>
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload("cnh", e.target.files[0])} />
        </label>
        <label data-testid="vistoria-foto-cnh-arquivo"
          className={`flex flex-col items-center gap-1.5 p-3.5 rounded-xl border-2 cursor-pointer text-center ${fotos.cnh ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300 bg-white"}`}>
          {uploading === "cnh" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <ScrollText className="w-5 h-5 text-navy" />}
          <span className="text-[10px] font-bold text-navy">📁 Enviar arquivo/PDF</span>
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload("cnh", e.target.files[0])} />
        </label>
      </div>
      {fotos.cnh && (
        <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1.5 mb-4 -mt-2" data-testid="vistoria-cnh-ok">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" /> CNH enviada ✓ {String(fotos.cnh).endsWith(".pdf") ? "(arquivo PDF)" : "(foto)"}
        </p>
      )}

      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">CRLV — documento do veículo (câmera ou arquivo/PDF) *</p>
      <div className="grid grid-cols-2 gap-2 mb-4" data-testid="vistoria-crlv-opcoes">
        <label data-testid="vistoria-foto-crlv-camera"
          className={`flex flex-col items-center gap-1.5 p-3.5 rounded-xl border-2 cursor-pointer text-center ${fotos.crlv ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300 bg-white"}`}>
          {uploading === "crlv" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <Camera className="w-5 h-5 text-navy" />}
          <span className="text-[10px] font-bold text-navy">📷 Tirar foto do CRLV</span>
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload("crlv", e.target.files[0])} />
        </label>
        <label data-testid="vistoria-foto-crlv-arquivo"
          className={`flex flex-col items-center gap-1.5 p-3.5 rounded-xl border-2 cursor-pointer text-center ${fotos.crlv ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300 bg-white"}`}>
          {uploading === "crlv" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <ScrollText className="w-5 h-5 text-navy" />}
          <span className="text-[10px] font-bold text-navy">📁 Enviar arquivo/PDF</span>
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload("crlv", e.target.files[0])} />
        </label>
      </div>
      {fotos.crlv && (
        <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1.5 mb-4 -mt-2" data-testid="vistoria-crlv-ok">
          <CheckCircle2 className="w-4 h-4 text-emerald-500" /> CRLV enviado ✓ {String(fotos.crlv).endsWith(".pdf") ? "(arquivo PDF)" : "(foto)"}
        </p>
      )}

      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Fotos do veículo pela câmera (5)</p>
      <div className="space-y-2 mb-5">
        {FOTOS_VEICULO.map(([k, label]) => (
          <label key={k} data-testid={`vistoria-foto-${k}`}
            className={`flex items-center gap-3 w-full p-3.5 rounded-xl border-2 cursor-pointer active:scale-[0.98] transition ${
              fotos[k] ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300 bg-white"}`}>
            {uploading === k ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" />
              : fotos[k] ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              : <Camera className="w-5 h-5 text-navy" />}
            <span className="text-xs font-semibold text-navy flex-1">{label}{fotos[k] ? " — enviada ✓" : ""}</span>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(k, e.target.files[0])} />
          </label>
        ))}
      </div>

      <button data-testid="enviar-vistoria-btn" onClick={enviar} disabled={!completo || busy}
        className="w-full py-4 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-50">
        {busy ? "Enviando..." : "ENVIAR VISTORIA PARA ANÁLISE"}
      </button>
    </div>
  );
}

function TermoScreen({ onAceito, onCancelar, modo = "aceite" }) {
  const [termo, setTermo] = useState(null);
  const [scrolledEnd, setScrolledEnd] = useState(false);
  const [declaro, setDeclaro] = useState(false);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/termo/vigente").then((r) => setTermo(r.data)).catch(() => toast.error("Falha ao carregar o termo"));
  }, []);

  const onScroll = () => {
    const el = scrollRef.current;
    if (el && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledEnd(true);
  };

  const aceitar = async () => {
    setBusy(true);
    try {
      await onAceito(termo.versao);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0b1220] flex justify-center" data-testid="termo-screen">
      <div className="w-full max-w-md bg-slate-50 flex flex-col">
        <div className="bg-navy px-5 py-4">
          <p className="font-heading font-extrabold text-white text-sm flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-[#F5B301]" /> Termo de Ciência e Responsabilidade
          </p>
          <p className="text-[11px] text-slate-300 mt-0.5">
            {termo ? `Versão ${termo.versao} · leia até o final para aceitar` : "Carregando..."}
          </p>
        </div>
        <div ref={scrollRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-5 py-4" data-testid="termo-texto-scroll">
          {termo ? (
            <pre className="text-[12px] text-slate-700 whitespace-pre-wrap leading-relaxed font-sans">{termo.texto}</pre>
          ) : (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#F5B301]" /></div>
          )}
          {termo && !scrolledEnd && (
            <p className="text-center text-[10px] text-slate-400 py-3 animate-pulse">↓ role até o final do termo ↓</p>
          )}
        </div>
        <div className="px-5 py-4 bg-white border-t border-slate-200">
          {modo === "aceite" && (
            <label className={`flex items-start gap-3 mb-3 ${scrolledEnd ? "cursor-pointer" : "opacity-40"}`} data-testid="termo-declaro-label">
              <input data-testid="termo-declaro" type="checkbox" disabled={!scrolledEnd}
                checked={declaro} onChange={(e) => setDeclaro(e.target.checked)}
                className="mt-0.5 w-5 h-5 accent-[#F5B301]" />
              <span className="text-[11px] text-slate-600 leading-relaxed">
                Declaro que li integralmente o Termo de Ciência e Responsabilidade, compreendi as obrigações nele
                descritas e estou ciente das responsabilidades legais e contratuais que me cabem na execução dos
                serviços que eu aceitar realizar.
              </span>
            </label>
          )}
          <button data-testid="termo-aceitar-btn" onClick={aceitar}
            disabled={!scrolledEnd || (modo === "aceite" && !declaro) || busy || !termo}
            className="w-full py-4 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-40">
            {busy ? "Registrando..." : modo === "leitura" ? "CONCLUIR LEITURA DO CONTRATO" : "LI, ESTOU CIENTE E ACEITO OS TERMOS"}
          </button>
          {onCancelar && (
            <button data-testid="termo-voltar-btn" onClick={onCancelar} className="mt-2.5 w-full py-2.5 text-xs text-slate-400 underline">
              Voltar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function AguardandoAprovacao() {
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6" data-testid="aguardando-aprovacao">
      <div className="bg-white rounded-3xl border border-slate-100 shadow-md p-8 text-center w-full">
        <Hourglass className="w-12 h-12 text-[#F5B301] mx-auto mb-4" />
        <h2 className="font-heading text-xl font-bold text-navy">Cadastro em análise</h2>
        <p className="text-sm text-slate-500 mt-3 leading-relaxed">
          Seu cadastro está em análise pela equipe. Você poderá visualizar e receber corridas assim que a
          administração aprovar sua vistoria.
        </p>
        <span className="inline-block mt-4 text-[11px] font-bold bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full">
          AGUARDANDO APROVAÇÃO
        </span>
      </div>
    </div>
  );
}

function AvaliarClienteBloco({ job, token, onDone }) {
  const [estrelas, setEstrelas] = useState(0);
  const [comentario, setComentario] = useState("");
  const [busy, setBusy] = useState(false);
  const enviar = async () => {
    if (!estrelas) return toast.error("Selecione de 1 a 5 estrelas");
    setBusy(true);
    try {
      await api.post(`/driver/jobs/${job.job_id}/avaliar`, { estrelas, comentario }, { headers: hdr(token) });
      toast.success("Avaliação enviada!");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao avaliar");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 bg-gold-light border border-[#F5B301]/60 rounded-xl p-3" data-testid={`avaliar-cliente-${job.job_id}`}>
      <p className="text-xs font-bold text-navy mb-1.5">Avalie o cliente {job.cliente?.nome}:</p>
      <div className="flex gap-1.5 mb-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`estrela-cliente-${job.job_id}-${n}`} onClick={() => setEstrelas(n)}>
            <Star className={`w-7 h-7 ${n <= estrelas ? "fill-[#F5B301] text-[#F5B301]" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <input data-testid={`comentario-cliente-${job.job_id}`} className="w-full px-3 py-2 rounded-lg border border-slate-200 text-xs mb-2"
        placeholder="Comentário (opcional)" value={comentario} onChange={(e) => setComentario(e.target.value)} />
      <button data-testid={`enviar-avaliacao-cliente-${job.job_id}`} onClick={enviar} disabled={busy}
        className="w-full py-2.5 rounded-xl bg-navy text-[#F5B301] text-xs font-bold active:scale-95 transition-transform disabled:opacity-50">
        ENVIAR AVALIAÇÃO
      </button>
    </div>
  );
}

function TransitoBloco({ job, token }) {
  const [t, setT] = useState(null);
  useEffect(() => {
    api.get(`/driver/jobs/${job.job_id}/transito`, { headers: hdr(token) }).then((r) => setT(r.data)).catch(() => setT({ disponivel: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.job_id]);
  const NIVEL = { leve: ["🟢", "Trânsito leve"], moderado: ["🟡", "Trânsito moderado"], pesado: ["🔴", "Trânsito pesado"] };
  return (
    <div className="mt-1.5 text-[11px]" data-testid={`transito-${job.job_id}`}>
      {t?.disponivel ? (
        <p className="font-semibold text-slate-600">
          {NIVEL[t.nivel][0]} {NIVEL[t.nivel][1]} agora · estimado {t.duracao_transito_min} min até o destino
        </p>
      ) : (
        <p className="text-slate-400">Trânsito em tempo real: acompanhe no Google Maps ou Waze.</p>
      )}
    </div>
  );
}

function JobCard({ job, full, token, onAction, tracking, onToggleTracking, onOcorrencia, onStopAlert }) {
  const [chatOpen, setChatOpen] = useState(false);
  const [ajudaOpen, setAjudaOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); onAction(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
    finally { setBusy(false); }
  };
  const next = NEXT_STATUS[job.status];
  const active = ACTIVE.includes(job.status);
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`job-card-${job.job_id}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-heading font-bold text-navy text-sm">{job.tipo === "frete" ? "🚚 FRETE" : job.tipo === "entrega" ? "🏍️ ENTREGA" : "🏠 MUDANÇA"}</span>
        {job.urgente && <span className="text-[9px] font-extrabold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full" data-testid={`job-urgente-${job.job_id}`}>🚨 URGÊNCIA</span>}
        <span className="text-[10px] font-bold bg-navy text-[#F5B301] px-2 py-0.5 rounded-full" data-testid={`job-status-${job.job_id}`}>{STATUS_LABEL[job.status]}</span>
      </div>
      <p className="text-xs text-slate-600">{fmtD(job.data)} às {job.horario} · {job.distance_km ? `${job.distance_km} km` : ""}</p>
      <p className="text-xs text-slate-600 mt-0.5">{job.regiao_origem} → {job.regiao_destino}</p>
      <div className="mt-2 bg-slate-50 rounded-xl p-3 text-[11px] text-slate-600 space-y-0.5" data-testid={`job-itens-${job.job_id}`}>
        <p className="font-bold text-navy text-xs mb-1">Itens a carregar:</p>
        {Object.entries(job.itens || {}).map(([k, v]) => <p key={k}>• {k}: {v}</p>)}
        {job.outros_itens && <p>• Outros: {job.outros_itens}</p>}
        <p className="pt-1">Dificuldade: <strong>{job.dificuldade}</strong> · Ajudantes: <strong>{job.ajudantes || "não"}</strong></p>
        {job.escada?.existe && <p>⚠️ Escada: {job.escada.lances >= 4 ? "4+" : job.escada.lances} lance(s)</p>}
        {job.elevador?.existe && <p>Elevador: sim (comporta: {job.elevador.comporta})</p>}
        {job.acesso?.nivel && job.acesso.nivel !== "facil" && <p>⚠️ Acesso {job.acesso.nivel}</p>}
        {job.paradas_count > 0 && <p>🛑 {job.paradas_count} parada(s) intermediária(s)</p>}
      </div>
      <div className="mt-2 flex items-center justify-between bg-gold-light border border-[#F5B301]/60 rounded-xl px-3.5 py-2.5">
        <span className="text-[11px] font-bold text-navy">VOCÊ RECEBERÁ:</span>
        <span className="font-heading font-extrabold text-navy" data-testid={`job-valor-${job.job_id}`}>{brl(job.valor_motorista)}</span>
      </div>

      {job.status === "disponivel" && (
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button data-testid={`aceitar-corrida-${job.job_id}`} disabled={busy}
            onClick={() => { onStopAlert?.(job.job_id); act(() => api.post(`/driver/jobs/${job.job_id}/aceitar`, {}, { headers: hdr(token) }), "Corrida aceita!"); }}
            className="py-3 rounded-xl bg-emerald-600 text-white text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> ACEITAR CORRIDA
          </button>
          <button data-testid={`recusar-corrida-${job.job_id}`} disabled={busy}
            onClick={() => { onStopAlert?.(job.job_id); const motivo = window.prompt("Motivo (opcional):") || ""; act(() => api.post(`/driver/jobs/${job.job_id}/recusar`, { motivo }, { headers: hdr(token) }), "Recusa registrada"); }}
            className="py-3 rounded-xl bg-red-500 text-white text-xs font-bold flex items-center justify-center gap-1.5 active:scale-95 transition-transform disabled:opacity-50">
            <XCircle className="w-4 h-4" /> RECUSAR
          </button>
        </div>
      )}

      {full && active && (
        <div className="mt-3 space-y-2.5">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex gap-2" data-testid={`job-antecedencia-${job.job_id}`}>
            <Clock className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-700 leading-relaxed">
              <strong>ATENÇÃO:</strong> chegue ao local com pelo menos 15 minutos de antecedência.
              Serviço às <strong>{job.horario}</strong> → chegada obrigatória às <strong>{minus15(job.horario)}</strong>.
            </p>
          </div>
          <div className="text-[11px] text-slate-600 space-y-1 bg-slate-50 rounded-xl p-3">
            <p><strong>Origem:</strong> {addrTxt(job.origem)}</p>
            {(job.paradas || []).map((p, i) => <p key={i}><strong>Parada {i + 1}:</strong> {addrTxt(p)}</p>)}
            <p><strong>Destino:</strong> {addrTxt(job.destino)}</p>
            <p><strong>Cliente:</strong> {job.cliente?.nome} · {job.cliente?.telefone}</p>
          </div>
          {job.origem?.cidade && (
            <div>
              <iframe
                title="mapa-destino"
                data-testid={`job-mapa-${job.job_id}`}
                className="w-full h-36 rounded-xl border border-slate-200"
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${-43.4},${-23.1},${-43.0},${-22.7}&layer=mapnik`}
              />
              <TransitoBloco job={job} token={token} />
            </div>
          )}
          <p className="text-[10px] font-bold tracking-wider uppercase text-slate-400">Como você quer navegar?</p>
          <div className="grid grid-cols-2 gap-2">
            <a data-testid={`nav-origin-gmaps-${job.job_id}`} href={mapsUrl(job.origem)} target="_blank" rel="noopener noreferrer"
              className="py-2.5 rounded-xl bg-navy text-[#F5B301] text-[11px] font-bold flex items-center justify-center gap-1.5"><Navigation className="w-3.5 h-3.5" /> ORIGEM · MAPS</a>
            <a data-testid={`nav-origin-waze-${job.job_id}`} href={wazeUrl(job.origem)} target="_blank" rel="noopener noreferrer"
              className="py-2.5 rounded-xl bg-white border-2 border-navy text-navy text-[11px] font-bold flex items-center justify-center gap-1.5"><Navigation className="w-3.5 h-3.5" /> ORIGEM · WAZE</a>
            <a data-testid={`nav-dest-gmaps-${job.job_id}`} href={mapsUrl(job.destino)} target="_blank" rel="noopener noreferrer"
              className="py-2.5 rounded-xl bg-navy text-[#F5B301] text-[11px] font-bold flex items-center justify-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> DESTINO · MAPS</a>
            <a data-testid={`nav-dest-waze-${job.job_id}`} href={wazeUrl(job.destino)} target="_blank" rel="noopener noreferrer"
              className="py-2.5 rounded-xl bg-white border-2 border-navy text-navy text-[11px] font-bold flex items-center justify-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> DESTINO · WAZE</a>
          </div>
          <button data-testid={`tracking-toggle-${job.job_id}`} onClick={() => onToggleTracking(job)}
            className={`w-full py-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform ${
              tracking === job.job_id ? "bg-emerald-600 text-white" : "bg-slate-200 text-navy"}`}>
            <Satellite className="w-4 h-4" />
            {tracking === job.job_id ? "🟢 LOCALIZAÇÃO SENDO COMPARTILHADA — TOQUE PARA PARAR" : "📍 COMPARTILHAR LOCALIZAÇÃO DURANTE O SERVIÇO"}
          </button>
          <button data-testid={`ocorrencia-btn-${job.job_id}`} onClick={() => onOcorrencia(job)}
            className="w-full py-3 rounded-xl bg-red-100 border border-red-300 text-red-600 text-xs font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <Siren className="w-4 h-4" /> COMUNICAR OCORRÊNCIA
          </button>
          {next && (
            <button data-testid={`job-next-status-${job.job_id}`} disabled={busy}
              onClick={() => act(() => api.post(`/driver/jobs/${job.job_id}/status`, { status: next[0] }, { headers: hdr(token) }), "Status atualizado!")}
              className="w-full py-4 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-50">
              {next[1]}
            </button>
          )}
        </div>
      )}

      {full && job.motorista_id && ["aceita", "a_caminho", "chegou_origem", "carga_iniciada", "em_rota", "chegou_destino"].includes(job.status) && (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button data-testid={`chat-corrida-${job.job_id}`} onClick={() => setChatOpen(true)}
            className="py-2.5 rounded-xl bg-navy text-[#F5B301] text-[11px] font-extrabold active:scale-95 transition-transform">
            💬 CHAT DA CORRIDA
          </button>
          <button data-testid={`ajuda-corrida-${job.job_id}`} onClick={() => setAjudaOpen(true)}
            className="py-2.5 rounded-xl bg-amber-100 border border-amber-300 text-amber-700 text-[11px] font-extrabold active:scale-95 transition-transform">
            🆘 AJUDA DO ADM
          </button>
        </div>
      )}
      {full && job.status === "aceita" && (
        <button data-testid={`cancelar-corrida-${job.job_id}`} onClick={() => setCancelOpen(true)}
          className="mt-2 w-full py-2.5 rounded-xl bg-white border-2 border-red-200 text-red-600 text-[11px] font-extrabold active:scale-95 transition-transform">
          ❌ CANCELAR CORRIDA
        </button>
      )}
      {full && job.status === "concluida" && (
        <>
          <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-[11px] text-emerald-700" data-testid={`job-pagamento-${job.job_id}`}>
            💰 Meu pagamento: <strong>{brl(job.valor_motorista)}</strong> — {job.pagamento_motorista?.status === "realizado" ? `✅ PAGAMENTO REALIZADO em ${new Date(job.pagamento_motorista.pago_em).toLocaleString("pt-BR")}` : "PAGAMENTO PENDENTE"}
          </div>
          {job.recebimento && !job.recebimento.confirmado && (
            <button data-testid={`confirmar-recebimento-${job.job_id}`} disabled={busy}
              onClick={() => act(() => api.post(`/driver/jobs/${job.job_id}/confirmar-recebimento`, {}, { headers: hdr(token) }), "Recebimento do serviço confirmado!")}
              className="mt-3 w-full py-4 rounded-2xl bg-emerald-600 text-white font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-50">
              ✅ CONFIRMAR RECEBIMENTO DO SERVIÇO
            </button>
          )}
          {job.recebimento?.confirmado && (
            <p className="mt-3 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid={`recebimento-ok-${job.job_id}`}>
              ✅ Recebimento confirmado {job.recebimento.por === "adm" ? "pela administração" : "por você"} em {new Date(job.recebimento.em).toLocaleString("pt-BR")}
            </p>
          )}
          {(!job.recebimento || job.recebimento.confirmado) && !job.avaliado_motorista && <AvaliarClienteBloco job={job} token={token} onDone={onAction} />}
        </>
      )}
      {chatOpen && <ChatCorrida jobId={job.job_id} role="motorista" token={token} onClose={() => setChatOpen(false)} subtitulo={`${job.regiao_origem} → ${job.regiao_destino} · ${fmtD(job.data)} ${job.horario}`} />}
      {ajudaOpen && <AjudaModal job={job} token={token} onClose={() => setAjudaOpen(false)} />}
      {cancelOpen && <CancelarCorridaModal job={job} token={token} onClose={() => setCancelOpen(false)} onDone={onAction} />}
    </div>
  );
}

function CadastroMotorista({ onBack, onLogin }) {
  const [form, setForm] = useState(() => {
    const base = { nome: "", cpf: "", telefone: "", whatsapp: "", email: "", pix: "", login: "", senha: "", placa: "" };
    try { return { ...base, ...JSON.parse(sessionStorage.getItem("cjr_cad_form") || "{}") }; } catch { return base; }
  });
  const [portes, setPortes] = useState(() => { try { return JSON.parse(sessionStorage.getItem("cjr_cad_portes") || "[]"); } catch { return []; } });
  const [fotos, setFotos] = useState(() => { try { return JSON.parse(sessionStorage.getItem("cjr_cad_fotos") || "{}"); } catch { return {}; } });
  const [fotoPerfil, setFotoPerfil] = useState(() => sessionStorage.getItem("cjr_cad_perfil") || "");
  const [termoLeitura, setTermoLeitura] = useState(() => { try { return JSON.parse(sessionStorage.getItem("cjr_cad_termo") || "null"); } catch { return null; } });
  const [termoAceito, setTermoAceito] = useState(false);
  const [assinatura, setAssinatura] = useState("");
  const [showTermo, setShowTermo] = useState(false);
  const [uploading, setUploading] = useState("");
  const [busy, setBusy] = useState(false);
  const cpfBuscado = useRef("");
  const [rascunhoMsg, setRascunhoMsg] = useState("");

  useEffect(() => { try { sessionStorage.setItem("cjr_cad_form", JSON.stringify(form)); } catch {} }, [form]);
  useEffect(() => { try { sessionStorage.setItem("cjr_cad_portes", JSON.stringify(portes)); } catch {} }, [portes]);
  useEffect(() => { try { sessionStorage.setItem("cjr_cad_fotos", JSON.stringify(fotos)); } catch {} }, [fotos]);
  useEffect(() => { try { sessionStorage.setItem("cjr_cad_perfil", fotoPerfil); } catch {} }, [fotoPerfil]);
  useEffect(() => { try { sessionStorage.setItem("cjr_cad_termo", JSON.stringify(termoLeitura)); } catch {} }, [termoLeitura]);

  // Carrega rascunho salvo no servidor quando o CPF fica completo (continuar cadastro)
  useEffect(() => {
    const cpf = somenteDigitos(form.cpf);
    if (cpf.length !== 11 || cpfBuscado.current === cpf) return;
    cpfBuscado.current = cpf;
    api.get("/driver/cadastro/rascunho", { params: { cpf } }).then((r) => {
      if (r.data.ja_cadastrado) {
        toast.error("Este CPF já possui cadastro. Faça login ou fale com a administração.");
        return;
      }
      if (r.data.existe && r.data.dados) {
        const d = r.data.dados;
        setForm((f) => ({ ...f, nome: d.nome || "", telefone: d.telefone || "", whatsapp: d.whatsapp || "", email: d.email || "", pix: d.pix || "", login: d.login || "", placa: d.placa || "" }));
        if (Array.isArray(d.portes)) setPortes(d.portes);
        if (d.fotos) setFotos(d.fotos);
        if (d.foto_perfil) setFotoPerfil(d.foto_perfil);
        if (d.termo_versao) setTermoLeitura({ versao: d.termo_versao });
        toast.success("Rascunho anterior carregado! Continue de onde parou.");
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.cpf]);

  // Salva rascunho automaticamente (debounce) enquanto o CPF estiver completo
  useEffect(() => {
    const cpf = somenteDigitos(form.cpf);
    if (cpf.length !== 11) { setRascunhoMsg(""); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/driver/cadastro/rascunho", {
          cpf,
          dados: { nome: form.nome, telefone: form.telefone, whatsapp: form.whatsapp, email: form.email, pix: form.pix, login: form.login, placa: form.placa, portes, fotos, foto_perfil: fotoPerfil, termo_versao: termoLeitura?.versao || null },
        });
        setRascunhoMsg(r.data.ja_cadastrado ? "" : "✓ Rascunho salvo automaticamente — você pode sair e continuar depois pelo CPF");
      } catch {}
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, portes, fotos, fotoPerfil, termoLeitura]);

  const input = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#F5B301]";
  const togglePorte = (p) => setPortes((c) => (c.includes(p) ? c.filter((x) => x !== p) : [...c, p]));

  const upload = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const path = await uploadFoto(file, `cadastro:${key}`);
      if (key === "perfil") setFotoPerfil(path);
      else setFotos((f) => ({ ...f, [key]: path }));
    } catch (e) {
      toast.error(e.message || "Não foi possível enviar a foto. Tente novamente.");
    } finally {
      setUploading("");
    }
  };

  const completo =
    form.nome && form.telefone && form.login && form.senha.length >= 4 && form.placa &&
    portes.length >= 1 && !!termoLeitura && termoAceito && !!assinatura.trim() && FOTOS_VISTORIA.every(([k]) => fotos[k]);

  const enviar = async () => {
    if (!termoLeitura) {
      return toast.error("É necessário abrir e ler o Contrato de Parceria completo antes de concluir o cadastro.");
    }
    if (!termoAceito) {
      return toast.error("Marque 'Li e aceito o contrato de parceria' para concluir o cadastro.");
    }
    if (!assinatura.trim()) {
      return toast.error("Digite seu nome completo como assinatura eletrônica para concluir o cadastro.");
    }
    setBusy(true);
    try {
      const r = await api.post("/driver/registro", { ...form, portes, fotos, foto_perfil: fotoPerfil, termos_aceitos: true, termo_versao: termoLeitura.versao, termo_visualizado: true, assinatura: assinatura.trim() });
      ["cjr_cad_form", "cjr_cad_portes", "cjr_cad_fotos", "cjr_cad_perfil", "cjr_cad_termo"].forEach((k) => sessionStorage.removeItem(k));
      localStorage.setItem("driver_token", r.data.token);
      toast.success("Cadastro enviado para análise!");
      onLogin(r.data.driver);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no cadastro");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-navy">
      <HeaderMotorista driver={null} onLogout={null} />
      <div className="px-5 py-6 pb-16 max-w-md mx-auto" data-testid="cadastro-motorista">
        <h2 className="font-heading text-xl font-bold text-white">Cadastro de Motorista / Prestador</h2>
        <p className="text-xs text-slate-300 mt-1 mb-1">Após o envio, seu cadastro fica EM ANÁLISE até a aprovação do administrador.</p>
        <p className="text-[11px] text-[#F5B301] mb-4" data-testid="cadastro-rascunho-hint">💾 Informe seu CPF e o rascunho será salvo automaticamente — você pode sair e continuar depois.</p>

        <div className="bg-white rounded-2xl p-4 space-y-2.5 mb-4">
          {[["nome", "Nome completo *"], ["cpf", "CPF"], ["telefone", "Telefone *"], ["whatsapp", "WhatsApp"], ["email", "E-mail"], ["pix", "Chave Pix / dados bancários"], ["login", "Login (telefone ou e-mail) *"], ["senha", "Crie sua senha *"], ["placa", "Placa do veículo *"]].map(([k, ph]) => (
            k === "senha" ? (
              <SenhaInput key={k} testid={`cadastro-${k}`} className={input} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            ) : (
              <input key={k} data-testid={`cadastro-${k}`} className={input} placeholder={ph}
                type="text" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            )
          ))}
        </div>
        {rascunhoMsg && (
          <p className="text-[11px] text-emerald-300 font-semibold mb-4 -mt-2" data-testid="rascunho-salvo-msg">{rascunhoMsg}</p>
        )}

        <div className="bg-white rounded-2xl p-4 mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Quais fretes você aceita? *</p>
          <div className="grid grid-cols-3 gap-2">
            {PORTES.map(([v, l]) => (
              <button key={v} data-testid={`cadastro-porte-${v}`} onClick={() => togglePorte(v)}
                className={`py-3 rounded-xl border-2 text-xs font-bold active:scale-95 transition-all ${
                  portes.includes(v) ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 bg-white text-slate-500"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-4 mb-4 space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Foto de perfil (rosto)</p>
          <label data-testid="cadastro-foto-perfil" className={`flex items-center gap-3 w-full p-3 rounded-xl border-2 cursor-pointer ${fotoPerfil ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
            {uploading === "perfil" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : fotoPerfil ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Camera className="w-5 h-5 text-navy" />}
            <span className="text-xs font-semibold text-navy">Sua foto {fotoPerfil ? "— enviada ✓" : ""}</span>
            <input type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => upload("perfil", e.target.files[0])} />
          </label>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">Habilitação (CNH) *</p>
          <div className="grid grid-cols-2 gap-2" data-testid="cadastro-cnh-opcoes">
            <label data-testid="cadastro-foto-cnh-camera"
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer text-center ${fotos.cnh ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
              {uploading === "cnh" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <Camera className="w-5 h-5 text-navy" />}
              <span className="text-[10px] font-bold text-navy">📷 Tirar foto da CNH</span>
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload("cnh", e.target.files[0])} />
            </label>
            <label data-testid="cadastro-foto-cnh-arquivo"
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer text-center ${fotos.cnh ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
              {uploading === "cnh" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <ScrollText className="w-5 h-5 text-navy" />}
              <span className="text-[10px] font-bold text-navy">📁 Enviar arquivo/PDF (CNH Digital)</span>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload("cnh", e.target.files[0])} />
            </label>
          </div>
          {fotos.cnh && (
            <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1.5" data-testid="cadastro-cnh-ok">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> CNH enviada ✓ {String(fotos.cnh).endsWith(".pdf") ? "(arquivo PDF)" : "(foto)"}
            </p>
          )}
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">CRLV — documento do veículo *</p>
          <div className="grid grid-cols-2 gap-2" data-testid="cadastro-crlv-opcoes">
            <label data-testid="cadastro-foto-crlv-camera"
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer text-center ${fotos.crlv ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
              {uploading === "crlv" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <Camera className="w-5 h-5 text-navy" />}
              <span className="text-[10px] font-bold text-navy">📷 Tirar foto do CRLV</span>
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload("crlv", e.target.files[0])} />
            </label>
            <label data-testid="cadastro-foto-crlv-arquivo"
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer text-center ${fotos.crlv ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
              {uploading === "crlv" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <ScrollText className="w-5 h-5 text-navy" />}
              <span className="text-[10px] font-bold text-navy">📁 Enviar arquivo/PDF</span>
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload("crlv", e.target.files[0])} />
            </label>
          </div>
          {fotos.crlv && (
            <p className="text-[11px] text-emerald-700 font-semibold flex items-center gap-1.5" data-testid="cadastro-crlv-ok">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" /> CRLV enviado ✓ {String(fotos.crlv).endsWith(".pdf") ? "(arquivo PDF)" : "(foto)"}
            </p>
          )}
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">Vistoria do veículo — 5 fotos pela câmera *</p>
          {FOTOS_VEICULO.map(([k, label]) => (
            <label key={k} data-testid={`cadastro-foto-${k}`}
              className={`flex items-center gap-3 w-full p-3 rounded-xl border-2 cursor-pointer ${fotos[k] ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
              {uploading === k ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" />
                : fotos[k] ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                : <Camera className="w-5 h-5 text-navy" />}
              <span className="text-xs font-semibold text-navy flex-1">{label}{fotos[k] ? " ✓" : ""}</span>
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(k, e.target.files[0])} />
            </label>
          ))}
        </div>

        <div className="bg-white rounded-2xl p-4 mb-4" data-testid="cadastro-termos-label">
          <p className="text-xs font-extrabold text-navy uppercase tracking-wide mb-2">📄 Contrato de Parceria / Termos de Responsabilidade</p>
          <button data-testid="cadastro-ler-termo" onClick={() => setShowTermo(true)}
            className="w-full py-3.5 rounded-xl bg-navy text-[#F5B301] font-heading font-extrabold text-xs flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <ScrollText className="w-4 h-4" /> {termoLeitura ? `CONTRATO LIDO (versão ${termoLeitura.versao}) — LER NOVAMENTE` : "VER E LER CONTRATO DE PARCERIA"}
          </button>
          {termoLeitura && (
            <p className="text-[11px] text-emerald-700 font-semibold mt-2 flex items-center gap-1.5" data-testid="termo-lido-info">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> Contrato visualizado e lido até o final (versão {termoLeitura.versao}).
            </p>
          )}
          <label className={`flex items-start gap-3 mt-3 ${termoLeitura ? "cursor-pointer" : "opacity-40"}`} data-testid="cadastro-aceite-label">
            <input data-testid="cadastro-aceite" type="checkbox" disabled={!termoLeitura}
              checked={termoAceito} onChange={(e) => setTermoAceito(e.target.checked)}
              className="mt-0.5 w-5 h-5 accent-[#F5B301]" />
            <span className="text-xs text-slate-600 leading-relaxed">
              Li e aceito o contrato de parceria e os termos de responsabilidade.
            </span>
          </label>
          {!termoLeitura && (
            <p className="text-[10px] text-slate-400 mt-1.5">Abra o contrato e role até o final para habilitar o aceite.</p>
          )}
          {termoAceito && (
            <div className="mt-3" data-testid="assinatura-bloco">
              <label className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Assinatura eletrônica</label>
              <input data-testid="cadastro-assinatura" className="w-full mt-1 px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#F5B301]"
                placeholder="Digite seu nome completo" value={assinatura} onChange={(e) => setAssinatura(e.target.value)} />
              <p className="text-[10px] text-slate-400 mt-1">
                Ao digitar seu nome completo, você assina eletronicamente o contrato. O aceite é registrado com nome, data, horário e versão.
              </p>
            </div>
          )}
        </div>
        {showTermo && (
          <TermoScreen
            modo="leitura"
            onAceito={async (versao) => { setTermoLeitura({ versao }); setShowTermo(false); toast.success("Leitura do contrato concluída!"); }}
            onCancelar={() => setShowTermo(false)}
          />
        )}

        <button data-testid="cadastro-enviar-btn" onClick={enviar} disabled={!completo || busy}
          className="w-full py-4 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-50">
          {busy ? "Enviando..." : "ENVIAR CADASTRO"}
        </button>
        <button data-testid="cadastro-voltar-btn" onClick={onBack} className="mt-3 w-full py-3 text-xs text-slate-300 underline">
          Já tenho cadastro — voltar ao login
        </button>
      </div>
    </div>
  );
}

function AlterarCadastro({ driver, token, onClose, onDone }) {
  const [form, setForm] = useState({
    nome: driver.nome || "", cpf: driver.cpf || "", telefone: driver.telefone || "",
    whatsapp: driver.whatsapp || "", email: driver.email || "", pix: driver.pix || "", placa: driver.placa || "",
  });
  const [portes, setPortes] = useState(driver.portes || []);
  const [fotos, setFotos] = useState(driver.vistoria || {});
  const [fotoPerfil, setFotoPerfil] = useState(driver.foto_perfil || "");
  const [uploading, setUploading] = useState("");
  const [busy, setBusy] = useState(false);

  const input = "w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#F5B301]";
  const togglePorte = (p) => setPortes((c) => (c.includes(p) ? c.filter((x) => x !== p) : [...c, p]));

  const upload = async (key, file) => {
    if (!file) return;
    setUploading(key);
    try {
      const path = await uploadFoto(file, `alteracao:${key}`);
      if (key === "perfil") setFotoPerfil(path);
      else setFotos((f) => ({ ...f, [key]: path }));
    } catch (e) {
      toast.error(e.message || "Não foi possível enviar a foto. Tente novamente.");
    } finally {
      setUploading("");
    }
  };

  const completo = form.nome.trim() && form.telefone.trim() && form.placa.trim() && portes.length >= 1 && !!fotos.cnh && !!fotos.crlv;

  const enviar = async () => {
    setBusy(true);
    try {
      const r = await api.post("/driver/cadastro/alterar", { ...form, portes, fotos, foto_perfil: fotoPerfil }, { headers: hdr(token) });
      if (r.data.alterados?.length) {
        toast.success("Alterações enviadas! Seu cadastro voltou para análise da administração.");
        onDone();
      } else {
        toast.info("Nenhuma alteração identificada.");
        onClose();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar alterações");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0b1220] flex justify-center overflow-y-auto" data-testid="alterar-cadastro-screen">
      <div className="w-full max-w-md bg-slate-50 min-h-screen pb-16">
        <div className="bg-navy px-5 py-4 sticky top-0 z-10">
          <p className="font-heading font-extrabold text-white text-sm flex items-center gap-2">
            <FileEdit className="w-4 h-4 text-[#F5B301]" /> Alterar cadastro
          </p>
          <p className="text-[11px] text-slate-300 mt-0.5">Ao enviar, seu cadastro volta para análise e você aguarda nova aprovação.</p>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div className="bg-white rounded-2xl p-4 space-y-2.5">
            {[["nome", "Nome completo *"], ["cpf", "CPF"], ["telefone", "Telefone *"], ["whatsapp", "WhatsApp"], ["email", "E-mail"], ["pix", "Chave Pix / dados bancários"], ["placa", "Placa do veículo *"]].map(([k, ph]) => (
              <input key={k} data-testid={`alteracao-${k}`} className={input} placeholder={ph}
                type="text" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
            ))}
          </div>
          <div className="bg-white rounded-2xl p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Portes de frete *</p>
            <div className="grid grid-cols-3 gap-2">
              {PORTES.map(([v, l]) => (
                <button key={v} data-testid={`alteracao-porte-${v}`} onClick={() => togglePorte(v)}
                  className={`py-3 rounded-xl border-2 text-xs font-bold active:scale-95 transition-all ${
                    portes.includes(v) ? "border-[#F5B301] bg-gold-light text-navy" : "border-slate-200 bg-white text-slate-500"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 space-y-2">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Foto de perfil (rosto)</p>
            <label data-testid="alteracao-foto-perfil" className={`flex items-center gap-3 w-full p-3 rounded-xl border-2 cursor-pointer ${fotoPerfil ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
              {uploading === "perfil" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : fotoPerfil ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <Camera className="w-5 h-5 text-navy" />}
              <span className="text-xs font-semibold text-navy">Sua foto {fotoPerfil ? "— enviada ✓" : ""}</span>
              <input type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => upload("perfil", e.target.files[0])} />
            </label>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">Habilitação (CNH) *</p>
            <div className="grid grid-cols-2 gap-2" data-testid="alteracao-cnh-opcoes">
              <label data-testid="alteracao-foto-cnh-camera"
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer text-center ${fotos.cnh ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
                {uploading === "cnh" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <Camera className="w-5 h-5 text-navy" />}
                <span className="text-[10px] font-bold text-navy">📷 Tirar foto da CNH</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload("cnh", e.target.files[0])} />
              </label>
              <label data-testid="alteracao-foto-cnh-arquivo"
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer text-center ${fotos.cnh ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
                {uploading === "cnh" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <ScrollText className="w-5 h-5 text-navy" />}
                <span className="text-[10px] font-bold text-navy">📁 Enviar arquivo/PDF</span>
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload("cnh", e.target.files[0])} />
              </label>
            </div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">CRLV — documento do veículo *</p>
            <div className="grid grid-cols-2 gap-2" data-testid="alteracao-crlv-opcoes">
              <label data-testid="alteracao-foto-crlv-camera"
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer text-center ${fotos.crlv ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
                {uploading === "crlv" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <Camera className="w-5 h-5 text-navy" />}
                <span className="text-[10px] font-bold text-navy">📷 Tirar foto do CRLV</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload("crlv", e.target.files[0])} />
              </label>
              <label data-testid="alteracao-foto-crlv-arquivo"
                className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 cursor-pointer text-center ${fotos.crlv ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
                {uploading === "crlv" ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <ScrollText className="w-5 h-5 text-navy" />}
                <span className="text-[10px] font-bold text-navy">📁 Enviar arquivo/PDF</span>
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload("crlv", e.target.files[0])} />
              </label>
            </div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-2">Fotos do veículo (câmera)</p>
            {FOTOS_VEICULO.map(([k, label]) => (
              <label key={k} data-testid={`alteracao-foto-${k}`}
                className={`flex items-center gap-3 w-full p-3 rounded-xl border-2 cursor-pointer ${fotos[k] ? "border-emerald-300 bg-emerald-50" : "border-dashed border-slate-300"}`}>
                {uploading === k ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" />
                  : fotos[k] ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  : <Camera className="w-5 h-5 text-navy" />}
                <span className="text-xs font-semibold text-navy flex-1">{label}{fotos[k] ? " ✓" : ""}</span>
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(k, e.target.files[0])} />
              </label>
            ))}
          </div>
          <button data-testid="alteracao-enviar-btn" onClick={enviar} disabled={!completo || busy}
            className="w-full py-4 rounded-2xl bg-[#F5B301] text-navy font-heading font-extrabold text-sm active:scale-95 transition-transform disabled:opacity-50">
            {busy ? "Enviando..." : "ENVIAR ALTERAÇÕES PARA ANÁLISE"}
          </button>
          <button data-testid="alteracao-cancelar-btn" onClick={onClose}
            className="w-full py-3 rounded-2xl bg-white border border-slate-200 text-slate-500 font-heading font-bold text-xs active:scale-95 transition-transform">
            CANCELAR
          </button>
        </div>
      </div>
    </div>
  );
}

const OCORRENCIA_TIPOS = ["Acidente", "Avaria/dano na carga", "Perda/extravio", "Roubo/furto", "Atraso relevante", "Divergência de itens/volumes", "Problema de acesso", "Carga não informada", "Outro"];

function OcorrenciaModal({ job, token, onClose, onDone }) {
  const [tipo, setTipo] = useState(OCORRENCIA_TIPOS[0]);
  const [descricao, setDescricao] = useState("");
  const [fotos, setFotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file || fotos.length >= 6) return;
    setUploading(true);
    try {
      const path = await uploadFoto(file, "ocorrencia");
      setFotos((f) => [...f, path]);
    } catch (e) {
      toast.error(e.message || "Não foi possível enviar a foto. Tente novamente.");
    } finally {
      setUploading(false);
    }
  };

  const enviar = async () => {
    if (!descricao.trim()) return toast.error("Descreva a ocorrência");
    setBusy(true);
    try {
      await api.post(`/driver/jobs/${job.job_id}/ocorrencia`, { tipo, descricao, fotos }, { headers: hdr(token) });
      toast.success("Ocorrência registrada e enviada à administração!");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao registrar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" data-testid="ocorrencia-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl sm:rounded-2xl p-5 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto">
        <p className="font-heading font-extrabold text-navy text-sm flex items-center gap-2">
          <Siren className="w-4 h-4 text-red-500" /> Comunicar ocorrência
        </p>
        <p className="text-[11px] text-slate-500">
          Corrida {job.tipo === "frete" ? "frete" : "mudança"} · {job.regiao_origem} → {job.regiao_destino} · {fmtD(job.data)} às {job.horario}
        </p>
        <div>
          <label className="text-[11px] font-bold text-slate-400 uppercase">Tipo de ocorrência</label>
          <select data-testid="ocorrencia-tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}
            className="w-full mt-1 px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm">
            {OCORRENCIA_TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-400 uppercase">Descrição *</label>
          <textarea data-testid="ocorrencia-descricao" rows={4} value={descricao} onChange={(e) => setDescricao(e.target.value)}
            placeholder="Descreva o que aconteceu, local aproximado e providências tomadas..."
            className="w-full mt-1 px-4 py-3 rounded-xl border border-slate-200 text-sm" />
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-400 uppercase">Fotos (até 6, opcional)</label>
          <label data-testid="ocorrencia-foto" className="mt-1 flex items-center gap-3 w-full p-3 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer">
            {uploading ? <Loader2 className="w-5 h-5 animate-spin text-[#F5B301]" /> : <Camera className="w-5 h-5 text-navy" />}
            <span className="text-xs font-semibold text-navy">Adicionar foto ({fotos.length}/6)</span>
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files[0])} />
          </label>
          {fotos.length > 0 && (
            <div className="flex gap-1.5 mt-2 flex-wrap">
              {fotos.map((f, i) => (
                <img key={i} src={`${API}/files/${f}`} alt="" className="w-14 h-14 object-cover rounded-lg border border-slate-200" />
              ))}
            </div>
          )}
        </div>
        <p className="text-[10px] text-slate-400">A ocorrência fica registrada com data, horário e vínculo à corrida, motorista e cliente.</p>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="ocorrencia-enviar" onClick={enviar} disabled={busy}
            className="py-3.5 rounded-xl bg-red-600 text-white font-heading font-extrabold text-xs disabled:opacity-50">
            {busy ? "Enviando..." : "ENVIAR OCORRÊNCIA"}
          </button>
          <button data-testid="ocorrencia-cancelar" onClick={onClose}
            className="py-3.5 rounded-xl bg-slate-100 text-slate-500 font-heading font-bold text-xs">CANCELAR</button>
        </div>
      </div>
    </div>
  );
}

export function ChatCorrida({ jobId, role, token, onClose, subtitulo }) {
  const [msgs, setMsgs] = useState([]);
  const [texto, setTexto] = useState("");
  const [aberto, setAberto] = useState(true);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const base = role === "motorista" ? `/driver/jobs/${jobId}/chat` : role === "adm" ? `/admin/jobs/${jobId}/chat` : `/cliente/jobs/${jobId}/chat`;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = async () => {
    try {
      const r = await api.get(base, { headers });
      setMsgs(r.data.mensagens || []);
      if (role !== "adm") setAberto(r.data.chat_aberto !== false);
    } catch {}
  };
  useEffect(() => { load(); const i = setInterval(load, 4000); return () => clearInterval(i); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const enviar = async () => {
    if (!texto.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(base, { texto }, { headers });
      setTexto("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao enviar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0b1220]/95 flex justify-center" data-testid="chat-corrida-modal">
      <div className="w-full max-w-md flex flex-col h-full">
        <div className="bg-navy px-4 py-3.5 flex items-center gap-3">
          <button data-testid="chat-fechar-btn" onClick={onClose} className="text-slate-400 text-lg font-bold px-1">✕</button>
          <div className="flex-1">
            <p className="font-heading font-extrabold text-white text-sm">💬 Chat da corrida</p>
            {subtitulo && <p className="text-[10px] text-slate-400">{subtitulo}</p>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5 bg-slate-100" data-testid="chat-mensagens">
          {msgs.length === 0 && <p className="text-center text-xs text-slate-400 py-10">Nenhuma mensagem ainda. Diga olá!</p>}
          {msgs.map((m) => {
            const mine = m.de === role || (role === "motorista" && m.de === "motorista") || (role === "cliente" && m.de === "cliente");
            const isAdm = m.de === "adm";
            return (
              <div key={m.msg_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.msg_id}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${
                  isAdm ? "bg-[#F5B301] text-navy" : mine ? "bg-navy text-white" : "bg-white text-slate-700 border border-slate-200"}`}>
                  <p className="text-[9px] font-bold opacity-70 mb-0.5">
                    {isAdm ? "🛡️ ADMINISTRAÇÃO" : m.de_nome} · {new Date(m.em).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </p>
                  <p className="text-xs leading-relaxed whitespace-pre-wrap">{m.texto}</p>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>
        {aberto ? (
          <div className="bg-white border-t border-slate-200 p-3 flex gap-2">
            <input data-testid="chat-input" className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5B301]"
              placeholder="Escreva uma mensagem..." value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") enviar(); }} />
            <button data-testid="chat-enviar-btn" onClick={enviar} disabled={busy || !texto.trim()}
              className="px-5 rounded-xl bg-[#F5B301] text-navy font-heading font-extrabold text-xs active:scale-95 transition-transform disabled:opacity-50">
              ENVIAR
            </button>
          </div>
        ) : (
          <div className="bg-slate-200 p-3 text-center text-[11px] font-semibold text-slate-500" data-testid="chat-readonly">
            Corrida finalizada — chat em modo somente leitura (histórico preservado).
          </div>
        )}
      </div>
    </div>
  );
}

const MOTIVOS_CANCELAMENTO = ["Problema no veículo", "Problema pessoal", "Imprevisto", "Indisponibilidade", "Problema de horário", "Outro motivo"];

function CancelarCorridaModal({ job, token, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);
  const confirmar = async () => {
    if (!window.confirm("Você tem certeza que deseja cancelar esta corrida? A corrida do cliente continuará ativa e será oferecida a outro motorista.")) return;
    setBusy(true);
    try {
      const r = await api.post(`/driver/jobs/${job.job_id}/cancelar-motorista`, { motivo, observacao: obs }, { headers: hdr(token) });
      toast.success(r.data.ultima_hora
        ? "Cancelamento de última hora registrado — o ADM foi alertado com prioridade para encontrar outro motorista."
        : "Cancelamento registrado. A corrida segue ativa e será oferecida a outro motorista.");
      onClose();
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao cancelar");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" data-testid="cancelar-corrida-modal">
      <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-5 space-y-3">
        <h3 className="font-heading font-extrabold text-navy text-base">❌ Cancelar corrida</h3>
        <p className="text-[11px] text-slate-500">A corrida do cliente <strong>não será cancelada</strong> — ela voltará para distribuição e outro motorista poderá assumir. Se faltarem menos de 24h, o ADM recebe alerta prioritário.</p>
        <div className="grid grid-cols-2 gap-1.5">
          {MOTIVOS_CANCELAMENTO.map((m) => (
            <button key={m} data-testid={`cancel-motivo-${m}`} onClick={() => setMotivo(m)}
              className={`py-2 px-1 rounded-lg border-2 text-[10px] font-bold active:scale-95 transition-all ${motivo === m ? "border-red-400 bg-red-50 text-red-700" : "border-slate-200 text-slate-500"}`}>
              {m}
            </button>
          ))}
        </div>
        <textarea data-testid="cancel-obs" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" rows={2}
          placeholder="Observação complementar (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} />
        <button data-testid="cancel-confirmar-btn" onClick={confirmar} disabled={!motivo || busy}
          className="w-full py-3.5 rounded-2xl bg-red-600 text-white font-heading font-extrabold text-xs active:scale-95 transition-transform disabled:opacity-50">
          {busy ? "Registrando..." : "CONFIRMAR CANCELAMENTO"}
        </button>
        <button data-testid="cancel-voltar-btn" onClick={onClose}
          className="w-full py-2.5 rounded-2xl bg-slate-100 text-slate-500 font-heading font-bold text-xs active:scale-95 transition-transform">
          VOLTAR
        </button>
      </div>
    </div>
  );
}

const MOTIVOS_AJUDA = ["Cliente não responde", "Cliente não visualizou", "Não consigo contato", "Preciso de ajuda", "Problema com informações", "Outro motivo"];

function AjudaModal({ job, token, onClose }) {
  const [motivo, setMotivo] = useState("");
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);
  const enviar = async () => {
    setBusy(true);
    try {
      await api.post(`/driver/jobs/${job.job_id}/pedir-ajuda`, { motivo, observacao: obs }, { headers: hdr(token) });
      toast.success("Solicitação enviada! O ADM foi alertado e pode acompanhar o chat da corrida.");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao solicitar ajuda");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" data-testid="ajuda-modal">
      <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-5 space-y-3">
        <h3 className="font-heading font-extrabold text-navy text-base">🆘 Não consigo contato com o cliente</h3>
        <p className="text-[11px] text-slate-500">O ADM receberá um alerta com os dados da corrida e poderá visualizar o chat para ajudar.</p>
        <div className="grid grid-cols-2 gap-1.5">
          {MOTIVOS_AJUDA.map((m) => (
            <button key={m} data-testid={`ajuda-motivo-${m}`} onClick={() => setMotivo(m)}
              className={`py-2 px-1 rounded-lg border-2 text-[10px] font-bold active:scale-95 transition-all ${motivo === m ? "border-amber-400 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-500"}`}>
              {m}
            </button>
          ))}
        </div>
        <textarea data-testid="ajuda-obs" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" rows={2}
          placeholder="Observação complementar (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} />
        <button data-testid="ajuda-enviar-btn" onClick={enviar} disabled={!motivo || busy}
          className="w-full py-3.5 rounded-2xl bg-amber-500 text-white font-heading font-extrabold text-xs active:scale-95 transition-transform disabled:opacity-50">
          {busy ? "Enviando..." : "SOLICITAR AJUDA DO ADM"}
        </button>
        <button onClick={onClose} className="w-full py-2.5 rounded-2xl bg-slate-100 text-slate-500 font-heading font-bold text-xs active:scale-95 transition-transform">
          VOLTAR
        </button>
      </div>
    </div>
  );
}

function TelaNovaCorrida({ job, token, onDecide }) {
  const [busy, setBusy] = useState(false);
  const itensCount = Object.values(job.itens || {}).reduce((a, b) => a + (Number(b) || 0), 0);
  const responder = async (acao) => {
    setBusy(true);
    try {
      if (acao === "aceitar") {
        await api.post(`/driver/jobs/${job.job_id}/aceitar`, {}, { headers: hdr(token) });
        toast.success("Corrida aceita! Bom trabalho!");
      } else {
        const motivo = window.prompt("Motivo da recusa (opcional):") || "";
        await api.post(`/driver/jobs/${job.job_id}/recusar`, { motivo }, { headers: hdr(token) });
        toast.success("Recusa registrada");
      }
      onDecide(job.job_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha");
      if ([404, 409].includes(e.response?.status)) onDecide(job.job_id);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#0b1220] flex justify-center overflow-y-auto" data-testid="tela-nova-corrida">
      <div className="w-full max-w-md px-5 py-8 space-y-4">
        <div className="text-center">
          <img src="/caminhao-amarelo.png" alt="" className="w-16 h-16 object-contain mx-auto mb-2 animate-bounce" />
          <h2 className="font-heading font-extrabold text-[#F5B301] text-2xl">NOVA CORRIDA!</h2>
          <p className="text-xs text-slate-400 mt-1">Responda para parar o alerta sonoro</p>
        </div>
        <div className="bg-white rounded-2xl p-4 space-y-2.5">
          <p className="font-heading font-extrabold text-navy text-lg text-center" data-testid="nova-corrida-valor">{brl(job.valor_motorista)}</p>
          <p className="text-center text-[10px] font-bold text-slate-400 uppercase -mt-1.5">Você receberá</p>
          <div className="border-t border-slate-100 pt-2.5 space-y-1.5 text-xs text-slate-600">
            <p data-testid="nova-corrida-tipo"><strong className="text-navy">{job.tipo === "frete" ? "🚚 FRETE" : job.tipo === "entrega" ? "🏍️ ENTREGA" : "🏠 MUDANÇA"}</strong> · {fmtD(job.data)} às {job.horario}</p>
            {job.urgente && (
              <p className="text-red-600 font-extrabold text-xs bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5" data-testid="nova-corrida-urgente">
                🚨 CORRIDA COM URGÊNCIA — valor adicional já incluso
              </p>
            )}
            <p data-testid="nova-corrida-cliente">👤 Cliente: <strong>{job.cliente?.nome || "—"}</strong></p>
            <p data-testid="nova-corrida-origem">📍 Embarque: {addrTxt(job.origem) || job.regiao_origem}</p>
            <p data-testid="nova-corrida-destino">🏁 Desembarque: {addrTxt(job.destino) || job.regiao_destino}</p>
            {job.distance_km != null && <p>🛣️ Distância: ~{job.distance_km} km</p>}
            {itensCount > 0 && <p>📦 Itens: {itensCount}</p>}
            <p>💪 Ajudantes: {job.ajudantes > 0 ? `${job.ajudantes} ajudante(s)` : "não precisa"}</p>
          </div>
        </div>
        <button data-testid="nova-corrida-aceitar-btn" disabled={busy} onClick={() => responder("aceitar")}
          className="w-full py-4 rounded-2xl bg-emerald-500 text-white font-heading font-extrabold text-base active:scale-95 transition-transform disabled:opacity-50">
          {busy ? "Enviando..." : "✅ ACEITAR CORRIDA"}
        </button>
        <button data-testid="nova-corrida-recusar-btn" disabled={busy} onClick={() => responder("recusar")}
          className="w-full py-4 rounded-2xl bg-red-600/90 text-white font-heading font-extrabold text-base active:scale-95 transition-transform disabled:opacity-50">
          ❌ RECUSAR CORRIDA
        </button>
      </div>
    </div>
  );
}

function DriverHome({ driver, token, onLogout, onChanged }) {
  const [tab, setTab] = useState("disponiveis");
  const [disponiveis, setDisponiveis] = useState([]);
  const [minhas, setMinhas] = useState([]);
  const [ganhos, setGanhos] = useState(null);
  const [fin, setFin] = useState([]);
  useEffect(() => {
    if (tab === "finalizadas") api.get("/driver/jobs/finalizadas", { headers: hdr(token) }).then((r) => setFin(r.data.jobs)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, minhas]);
  const [tracking, setTracking] = useState(null);
  const [novoFrete, setNovoFrete] = useState(null);
  const [ocorrenciaJob, setOcorrenciaJob] = useState(null);
  const [alterando, setAlterando] = useState(false);
  const watchRef = useRef(null);
  const lastSent = useRef(0);
  const prevCount = useRef(null);
  const alertedRef = useRef(new Set());
  const hornAudioRef = useRef(null);
  const vozAudioRef = useRef(null);
  const hornTimerRef = useRef(null);
  const alertaJobRef = useRef(null);
  const alertaParRef = useRef(0);

  // Alerta contínuo: alterna BUZINA de carreta e VOZ "Chegou uma nova corrida!", ~4s após cada som,
  // até o motorista ACEITAR ou RECUSAR (ou a corrida ficar indisponível). Timer único, sem sobreposição.
  const pararAlertaBuzina = () => {
    alertaJobRef.current = null;
    if (hornTimerRef.current) { clearTimeout(hornTimerRef.current); hornTimerRef.current = null; }
    [hornAudioRef.current, vozAudioRef.current].forEach((a) => { if (a) { try { a.pause(); a.currentTime = 0; } catch {} } });
  };

  const pararAlertaSeFor = (jobId) => { if (alertaJobRef.current === jobId) pararAlertaBuzina(); };

  const iniciarAlertaBuzina = (jobId) => {
    pararAlertaBuzina();
    alertaJobRef.current = jobId;
    alertaParRef.current = 0;
    const ciclo = () => {
      if (alertaJobRef.current !== jobId) return;
      const a = alertaParRef.current % 2 === 0 ? hornAudioRef.current : vozAudioRef.current;
      alertaParRef.current += 1;
      let espera = 6000;
      if (a) {
        try {
          a.currentTime = 0;
          a.play().catch(() => {});
          if (a.duration && isFinite(a.duration)) espera = a.duration * 1000 + 4000;
        } catch {}
      }
      hornTimerRef.current = setTimeout(ciclo, espera);
    };
    ciclo();
  };

  useEffect(() => {
    const buzina = new Audio("/buzina-carreta.mp3");
    buzina.preload = "auto";
    hornAudioRef.current = buzina;
    const voz = new Audio("/nova-corrida.mp3");
    voz.preload = "auto";
    vozAudioRef.current = voz;
    return () => pararAlertaBuzina();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Tempo real: nova corrida chega instantaneamente via WebSocket (sem esperar o poll de 30s)
  useEffect(() => {
    const close = connectWS((ev) => {
      if (ev.tipo === "nova_corrida" && ev.dados?.job_id) {
        const j = ev.dados;
        setDisponiveis((cur) => (cur.find((x) => x.job_id === j.job_id) ? cur : [j, ...cur]));
        if (alertedRef.current.has(j.job_id)) return;
        alertedRef.current.add(j.job_id);
        prevCount.current = (prevCount.current ?? 0) + 1;
        iniciarAlertaBuzina(j.job_id);
        setNovoFrete(j);
        setTab("disponiveis");
        navigator.vibrate?.([250, 100, 250]);
      } else if (ev.tipo === "corrida_indisponivel" && ev.dados?.job_id) {
        setDisponiveis((cur) => cur.filter((x) => x.job_id !== ev.dados.job_id));
        setNovoFrete((cur) => (cur?.job_id === ev.dados.job_id ? null : cur));
        pararAlertaSeFor(ev.dados.job_id);
      } else if (["recebimento_confirmado", "status_corrida"].includes(ev.tipo)) {
        load();
      }
    });
    return close;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    api.get("/driver/jobs/disponiveis", { headers: hdr(token) }).then((r) => {
      if (prevCount.current !== null && r.data.jobs.length > prevCount.current) {
        const novas = r.data.jobs.filter((j) => !alertedRef.current.has(j.job_id));
        if (novas.length) {
          iniciarAlertaBuzina(novas[0].job_id);
          navigator.vibrate?.([250, 100, 250]);
          novas.forEach((j) => alertedRef.current.add(j.job_id));
          setNovoFrete(novas[0]);
          setTab("disponiveis");
          if (typeof Notification !== "undefined" && Notification.permission === "granted" && document.visibilityState === "hidden") {
            const j = novas[0];
            try {
              new Notification("🚛 Novo frete disponível!", {
                body: `${j.tipo === "frete" ? "Frete" : "Mudança"} · ${fmtD(j.data)} às ${j.horario}\n${j.regiao_origem} → ${j.regiao_destino}\nVocê receberá: ${brl(j.valor_motorista)}`,
                icon: "/caminhao-amarelo.png",
                tag: `frete-${j.job_id}`,
              });
            } catch {}
          }
        }
      }
      prevCount.current = r.data.jobs.length;
      setDisponiveis(r.data.jobs);
      // Solicitação deixou de estar disponível (aceita por outro, cancelada, recusada etc.) → para o alerta
      if (alertaJobRef.current && !r.data.jobs.find((j) => j.job_id === alertaJobRef.current)) {
        pararAlertaBuzina();
      }
      setNovoFrete((cur) => (cur && !r.data.jobs.find((j) => j.job_id === cur.job_id) ? null : cur));
    }).catch(() => {});
    api.get("/driver/jobs/minhas", { headers: hdr(token) }).then((r) => setMinhas(r.data.jobs)).catch(() => {});
    api.get("/driver/ganhos", { headers: hdr(token) }).then((r) => setGanhos(r.data)).catch(() => {});
  };
  useEffect(() => {
    inscreverPush("/push/subscribe/driver");
    load();
    const i = setInterval(load, 30000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTracking = (job) => {
    if (tracking === job.job_id) {
      if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
      setTracking(null);
      toast.success("Compartilhamento de localização encerrado");
      return;
    }
    if (!navigator.geolocation) return toast.error("Geolocalização não suportada neste aparelho");
    const wid = navigator.geolocation.watchPosition(
      (pos) => {
        const now = Date.now();
        if (now - lastSent.current < 15000) return;
        lastSent.current = now;
        api.post(`/driver/jobs/${job.job_id}/location`, { lat: pos.coords.latitude, lng: pos.coords.longitude }, { headers: hdr(token) })
          .catch(() => toast.error("Não foi possível enviar a localização"));
      },
      () => {
        toast.error("Permissão de localização negada. O ADM será informado de que o recurso está indisponível.");
        setTracking(null);
      },
      { enableHighAccuracy: true }
    );
    watchRef.current = wid;
    setTracking(job.job_id);
    toast.success("Localização compartilhada durante o serviço");
  };

  useEffect(() => () => { if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current); }, []);

  return (
    <div className="min-h-screen bg-[#0b1220] flex justify-center">
      <div className="w-full max-w-md mx-auto min-h-screen bg-slate-50 shadow-2xl pb-8">
        <HeaderMotorista driver={driver} onLogout={onLogout} />

        <div className="px-4 pt-3">
          <button data-testid="alterar-cadastro-btn" onClick={() => setAlterando(true)}
            className="w-full py-2.5 rounded-xl bg-white border border-slate-200 text-navy text-xs font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <FileEdit className="w-4 h-4 text-[#F5B301]" /> ALTERAR CADASTRO
          </button>
        </div>

        <div className="px-4 pt-3 grid grid-cols-4 gap-2" data-testid="driver-tabs">
          {[["disponiveis", "Disponíveis"], ["minhas", "Minhas"], ["finalizadas", "Finalizadas"], ["ganhos", "Ganhos"]].map(([v, l]) => (
            <button key={v} data-testid={`driver-tab-${v}`} onClick={() => setTab(v)}
              className={`py-2.5 rounded-xl text-xs font-bold transition ${tab === v ? "bg-navy text-[#F5B301]" : "bg-white text-slate-500 border border-slate-200"}`}>
              {l}{v === "disponiveis" && disponiveis.length > 0 ? ` (${disponiveis.length})` : ""}
            </button>
          ))}
        </div>

        <div className="px-4 mt-4 space-y-3">
          {ocorrenciaJob && (
            <OcorrenciaModal job={ocorrenciaJob} token={token} onClose={() => setOcorrenciaJob(null)}
              onDone={() => { setOcorrenciaJob(null); load(); }} />
          )}
          {alterando && (
            <AlterarCadastro driver={driver} token={token} onClose={() => setAlterando(false)} onDone={onChanged} />
          )}
          {tab === "disponiveis" && novoFrete && (
            <TelaNovaCorrida job={novoFrete} token={token}
              onDecide={(jobId) => { pararAlertaSeFor(jobId); setNovoFrete(null); load(); }} />
          )}
          {tab === "disponiveis" && (
            disponiveis.length === 0
              ? <p className="text-center text-sm text-slate-400 py-16" data-testid="no-jobs-msg">Nenhuma corrida disponível no momento.</p>
              : disponiveis.map((j) => <JobCard key={j.job_id} job={j} full={false} token={token} onAction={load} tracking={tracking} onToggleTracking={toggleTracking} onOcorrencia={setOcorrenciaJob} onStopAlert={pararAlertaSeFor} />)
          )}
          {tab === "minhas" && (
            minhas.length === 0
              ? <p className="text-center text-sm text-slate-400 py-16" data-testid="no-my-jobs-msg">Você ainda não aceitou nenhuma corrida.</p>
              : minhas.map((j) => <JobCard key={j.job_id} job={j} full token={token} onAction={load} tracking={tracking} onToggleTracking={toggleTracking} onOcorrencia={setOcorrenciaJob} />)
          )}

          {tab === "finalizadas" && (
            fin.length === 0 ? <p className="text-center text-sm text-slate-400 py-10" data-testid="finalizadas-vazio">Nenhuma corrida finalizada ainda.</p>
              : fin.map((j) => <JobCard key={j.job_id} job={j} full token={token} onAction={load} tracking={tracking} onToggleTracking={toggleTracking} onOcorrencia={setOcorrenciaJob} />)
          )}
          {tab === "ganhos" && ganhos && (
            <div data-testid="driver-ganhos">
              <div className="grid grid-cols-2 gap-2.5">
                {[["Hoje", ganhos.totais.dia], ["Esta semana", ganhos.totais.semana], ["Este mês", ganhos.totais.mes], ["Acumulado", ganhos.totais.acumulado]].map(([l, v]) => (
                  <div key={l} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 text-center">
                    <Wallet className="w-5 h-5 text-[#F5B301] mx-auto mb-1" />
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{l}</p>
                    <p className="font-heading font-extrabold text-navy" data-testid={`ganhos-${l.toLowerCase().replace(" ", "-")}`}>{brl(v)}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs font-bold tracking-wider uppercase text-slate-400 mt-4 mb-2">Meus serviços</p>
              {ganhos.servicos.map((s) => (
                <div key={s.job_id} className="bg-white rounded-xl border border-slate-100 p-3 mb-2 flex items-center justify-between" data-testid={`ganho-row-${s.job_id}`}>
                  <div className="text-xs">
                    <p className="font-bold text-navy">{fmtD(s.data)} · {s.tipo === "frete" ? "Frete" : "Mudança"}</p>
                    <p className="text-slate-500">{s.regiao_origem} → {s.regiao_destino}</p>
                    <p className="text-[10px] text-slate-400">{STATUS_LABEL[s.status]}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-bold text-navy text-sm">{brl(s.valor_motorista)}</p>
                    <p className={`text-[10px] font-bold ${s.pagamento?.status === "realizado" ? "text-emerald-600" : "text-amber-600"}`}>
                      {s.pagamento?.status === "realizado" ? "✅ PAGO" : "PENDENTE"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DriverApp() {
  const [driver, setDriver] = useState(null);
  const [checking, setChecking] = useState(true);
  const [modo, setModo] = useState(
    new URLSearchParams(window.location.search).get("cadastro") ? "cadastro" : "login"
  );
  const token = localStorage.getItem("driver_token");

  const loadMe = (t) =>
    api.get("/driver/me", { headers: hdr(t) })
      .then((r) => setDriver(r.data))
      .catch(() => { localStorage.removeItem("driver_token"); setDriver(null); });

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    loadMe(token).finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    if (!window.confirm("Deseja realmente sair da conta?")) return;
    try { await api.post("/driver/logout", {}, { headers: hdr(token) }); } catch {}
    localStorage.removeItem("driver_token");
    setDriver(null);
    window.location.href = "/";
  };

  if (checking) {
    return <div className="min-h-screen bg-navy flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-[#F5B301]" /></div>;
  }
  if (!driver) {
    return modo === "cadastro"
      ? <CadastroMotorista onBack={() => setModo("login")} onLogin={setDriver} />
      : <Login onLogin={setDriver} onCadastro={() => setModo("cadastro")} />;
  }

  const onboarding = driver.onboarding_status || "ativo";

  if (driver.bloqueio) {
    const bl = driver.bloqueio;
    return (
      <div className="min-h-screen bg-[#0b1220] flex justify-center">
        <div className="w-full max-w-md mx-auto min-h-screen bg-slate-50 shadow-2xl">
          <HeaderMotorista driver={driver} onLogout={logout} />
          <div className="px-6 py-16 text-center" data-testid="driver-bloqueado">
            <Ban className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-navy">Cadastro bloqueado</h2>
            <p className="text-sm text-slate-500 mt-3 leading-relaxed">
              Seu cadastro foi bloqueado pela administração.
            </p>
            <p className="text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2.5 mt-3 font-semibold">
              Motivo: {bl.motivo}
            </p>
            <p className="text-[11px] text-slate-400 mt-2">
              {bl.tipo === "prazo" && bl.ate
                ? `Bloqueio com prazo até ${new Date(bl.ate).toLocaleDateString("pt-BR")}`
                : bl.tipo === "indefinido" ? "Bloqueio por tempo indefinido" : "Bloqueio temporário"}
              {" "}· Fale com a administração pelo WhatsApp.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (driver.termo_pendente) {
    return (
      <TermoScreen
        onAceito={async (versao) => {
          await api.post("/driver/termo/aceitar", { versao }, { headers: hdr(token) });
          toast.success("Novo termo aceito!");
          loadMe(token);
        }}
        onCancelar={null}
      />
    );
  }

  if (onboarding !== "ativo") {
    return (
      <div className="min-h-screen bg-[#0b1220] flex justify-center">
        <div className="w-full max-w-md mx-auto min-h-screen bg-slate-50 shadow-2xl">
          <HeaderMotorista driver={driver} onLogout={logout} />
          {onboarding === "aguardando_senha" && (
            <OnboardingSenha token={token} onDone={() => loadMe(token)} />
          )}
          {onboarding === "aguardando_vistoria" && (
            <OnboardingVistoria token={token} onDone={() => loadMe(token)} />
          )}
          {onboarding === "aguardando_aprovacao" && <AguardandoAprovacao />}
          {onboarding === "reprovado" && (
            <div className="px-6 py-16 text-center" data-testid="driver-reprovado">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold text-navy">Cadastro reprovado</h2>
              <p className="text-sm text-slate-500 mt-3 leading-relaxed">
                {driver.reprovacao?.motivo
                  ? `Motivo: ${driver.reprovacao.motivo}`
                  : "Seu cadastro não foi aprovado. Entre em contato com a administração."}
              </p>
            </div>
          )}
          {onboarding === "correcao_solicitada" && (
            <div data-testid="driver-correcao">
              <div className="mx-5 mt-5 bg-blue-50 border border-blue-200 rounded-2xl p-4">
                <p className="text-xs font-bold text-blue-700">📝 Correção solicitada pela administração:</p>
                <p className="text-xs text-blue-600 mt-1">{driver.correcao?.mensagem}</p>
                <p className="text-[11px] text-slate-500 mt-2">Envie a vistoria novamente com as correções solicitadas.</p>
              </div>
              <OnboardingVistoria token={token} onDone={() => loadMe(token)} />
            </div>
          )}
          {onboarding === "alteracao_pendente" && (
            <div className="px-6 py-16 text-center" data-testid="driver-alteracao-pendente">
              <Hourglass className="w-12 h-12 text-[#F5B301] mx-auto mb-4" />
              <h2 className="font-heading text-xl font-bold text-navy">Alteração em análise</h2>
              <p className="text-sm text-slate-500 mt-3 leading-relaxed">
                Suas alterações foram enviadas e estão em análise pela administração. Você voltará a receber
                corridas assim que as alterações forem aprovadas.
              </p>
              <span className="inline-block mt-4 text-[11px] font-bold bg-amber-100 text-amber-700 px-3 py-1.5 rounded-full">
                AGUARDANDO REAPROVAÇÃO
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return <DriverHome driver={driver} token={token} onLogout={logout} onChanged={() => loadMe(token)} />;
}
